import { Module } from 'vuex';
import { NetworkSecurityInternetState } from '@/modules/tickets/requirement/network-security-internet/store/network-security-internet.state';
import { NetworkSecurityInternetActions } from '@/modules/tickets/requirement/network-security-internet/store/network-security-internet.actions';
import { NetworkSecurityInternetMutations } from '@/modules/tickets/requirement/network-security-internet/store/network-security-internet.mutations';
import { Contact } from '@/modules/contacts/shared/domain/contact';
import { UserGetters } from '@/modules/users/store/user.getters';
import ticketsRepository from '@/modules/tickets/shared/repositories/tickets.repository';
import { User } from '@/modules/users/domain/user';
import { Tickets } from '@/modules/shared/shared/domain/constants/constants';
import { Typing, TypingLevels } from '@/modules/tickets/shared/domain/typing';
import { Exception } from '@/modules/shared/shared/domain/exceptions/exception';
import { NetworkSecurityInternetGetters } from '@/modules/tickets/requirement/network-security-internet/store/network-security-internet.getters';
import { NetworkSecurityInternetGeneralInformationData } from '@/modules/tickets/requirement/network-security-internet/domain/network-security-internet-general-information';
import { CategoryTyping } from '@/modules/tickets/shared/domain/category-typing';
import { NetworkSecurityInternetTicket } from '@/modules/tickets/requirement/network-security-internet/domain/network-security-internet-ticket';
import { EventBus } from '@/main';
import { EventBusMessages } from '@/modules/shared/shared/domain/constants/event-bus-messages';

const networkSecurityAndInternet: Module<NetworkSecurityInternetState, any> = {
    namespaced: true,
    state: {
        formValues: {
            generalInformation: {
                category: null,
                subCategory: null,
                subject: null,
            },
            categoriesTypings: [],
        },
    },
    getters: {
        [NetworkSecurityInternetGetters.GET_FORM_VALUES](
            state,
        ): {
            generalInformation: NetworkSecurityInternetGeneralInformationData;
            categoriesTypings: CategoryTyping;
        } {
            return state.formValues;
        },
    },
    mutations: {
        [NetworkSecurityInternetMutations.SET_FORM_VALUES](state, formValues) {
            state.formValues = formValues;
        },
        [NetworkSecurityInternetMutations.SET_CONTACT_INFORMATION](state, contact: Contact) {
            state.formValues.generalInformation.firstName = contact.name;
            state.formValues.generalInformation.lastName = contact.lastName;
            state.formValues.generalInformation.contactId = contact.id;
            state.formValues.generalInformation.idNumber = contact.ci;
            state.formValues.generalInformation.telephoneNumber = contact.phone;
        },
        [NetworkSecurityInternetMutations.SET_TYPINGS](state, typings: Typing[]) {
            state.formValues.categoriesTypings = typings.filter(
                typing => typing.level === TypingLevels.CATEGORY_LEVEL,
            );
            const subjects = typings.filter(typing => typing.level === TypingLevels.SUBJECT_LEVEL);
            const subCategories = typings.filter(
                typing => typing.level === TypingLevels.SUBCATEGORY_LEVEL,
            );

            state.formValues.categoriesTypings = state.formValues.categoriesTypings.map(
                category => {
                    return {
                        ...category,
                        subCategoriesTypings: subCategories
                            .filter(subCategory => subCategory.typing === category.id)
                            .map(subCategory => {
                                return {
                                    ...subCategory,
                                    subjectTypings: subjects.filter(
                                        subject => subject.typing === subCategory.id,
                                    ),
                                };
                            }),
                    };
                },
            );
        },
    },
    actions: {
        async [NetworkSecurityInternetActions.FETCH_VIEW]({
            commit,
            rootGetters,
        }): Promise<RequestResponse> {
            commit(NetworkSecurityInternetMutations.SET_FORM_VALUES, {
                generalInformation: {
                    category: null,
                    subCategory: null,
                    subject: null,
                },
                categoriesTypings: [],
            });
            const currentUser: User = rootGetters[`user/${UserGetters.GET_CURRENT_USER}`];
            commit(NetworkSecurityInternetMutations.SET_CONTACT_INFORMATION, currentUser.contact);
            try {
                const typings = await ticketsRepository.getTypings(
                    currentUser.contact.customer.id,
                    Tickets.NetworkSecurityAndInternet.name,
                );
                commit(NetworkSecurityInternetMutations.SET_TYPINGS, typings);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [NetworkSecurityInternetActions.CREATE_TICKET](
            { rootGetters },
            information: NetworkSecurityInternetGeneralInformationData,
        ): Promise<RequestResponse> {
            try {
                const currentUser: User = rootGetters[`user/${UserGetters.GET_CURRENT_USER}`];
                const contact = currentUser.contact;
                const customer = contact.customer;
                const ticket: NetworkSecurityInternetTicket = {
                    description: information.description || '',
                    typing: information.subject!,
                    ticketType: Tickets.NetworkSecurityAndInternet,
                    customer: {
                        id: customer.id,
                        name: customer.name,
                        category: customer.category!,
                        contact: {
                            id: contact.id,
                            name: contact.name,
                            lastName: contact.lastName,
                            email: contact.email,
                            ci: contact.ci!,
                        },
                    },
                };
                const id = (await ticketsRepository.createTicket(ticket)).data.id;
                EventBus.$emit(
                    EventBusMessages.SHOW_SUCCESS,
                    `Su ticket fue creado exitosamente (Serial Nº ${id})`,
                );
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },
    },
};

export default networkSecurityAndInternet;
